/**
 * GPS Tracking API Service
 * Handles all GPS-related API calls
 */

import apiService, { apiFetchBlob } from '../apiService';
import { API_ENDPOINTS } from '../config';
import type {
  GPSDevice,
  Geofence,
  GeofenceInput,
  LivePosition,
  TripReport,
  RouteReport,
  SummaryReport,
  StopsReport,
  AlertEvent,
  AlertRule,
  ReportFilters,
  DateRange,
} from '../../types/gps';

const GPS_BASE_URL = API_ENDPOINTS.GPS_TRACKING;

/**
 * Device API (Read-only - management in admin)
 */
export const deviceAPI = {
  /**
   * Get all GPS devices for the company
   */
  async listDevices(companyId?: number | null): Promise<GPSDevice[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/devices/`, {
      companyId: companyId ?? undefined,
    });
    return response.data;
  },

  /**
   * Search GPS devices with pagination
   */
  async searchDevices(params: {
    page?: number;
    page_size?: number;
    search?: string;
  }): Promise<{
    results: GPSDevice[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    next: boolean;
    previous: boolean;
  }> {
    const response = await apiService.get(`${GPS_BASE_URL}/devices/search/`, { params });
    return response.data;
  },

  /**
   * Get a specific GPS device
   */
  async getDevice(id: number): Promise<GPSDevice> {
    const response = await apiService.get(`${GPS_BASE_URL}/devices/${id}/`);
    return response.data;
  },

  /**
   * Sync device position from Traccar
   */
  async syncPosition(id: number): Promise<{ message: string; position: any }> {
    const response = await apiService.post(`${GPS_BASE_URL}/devices/${id}/sync_position/`);
    return response.data;
  },

  /**
   * Assign device to a vehicle
   */
  async assignVehicle(deviceId: number, vehicleId: number): Promise<{
    message: string;
    assignment: any;
  }> {
    const response = await apiService.post(
      `${GPS_BASE_URL}/devices/${deviceId}/assign_vehicle/`,
      { vehicle_id: vehicleId }
    );
    return response.data;
  },

  /**
   * Unassign device from vehicle
   */
  async unassignVehicle(deviceId: number): Promise<{ message: string }> {
    const response = await apiService.post(
      `${GPS_BASE_URL}/devices/${deviceId}/unassign_vehicle/`
    );
    return response.data;
  },

  /**
   * Accept GPS usage contract for a device
   */
  async acceptContract(deviceId: number): Promise<{ message: string; device: GPSDevice }> {
    const response = await apiService.post(
      `${GPS_BASE_URL}/devices/${deviceId}/accept-contract/`
    );
    return response.data;
  },
};

/**
 * Live Tracking API
 */
export const liveTrackingAPI = {
  /**
   * Get current positions for all company devices
   */
  async getCurrentPositions(): Promise<LivePosition[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/live-tracking/current_positions/`);
    return response.data;
  },

  /**
   * Get specific device position
   */
  async getDevicePosition(id: number, sync: boolean = false): Promise<LivePosition> {
    const params = sync ? { sync: 'true' } : {};
    const response = await apiService.get(
      `${GPS_BASE_URL}/live-tracking/${id}/device_position/`,
      { params }
    );
    return response.data;
  },

  /**
   * Get position history for a device
   */
  async getPositionHistory(id: number, hours: number = 24): Promise<{
    device_id: number;
    device_label: string;
    positions: any[];
    from: string;
    to: string;
  }> {
    const response = await apiService.get(
      `${GPS_BASE_URL}/live-tracking/${id}/position_history/`,
      { params: { hours } }
    );
    return response.data;
  },
};

/**
 * Geofence API (Full CRUD)
 */
export const geofenceAPI = {
  /**
   * Get all geofences for the company
   */
  async listGeofences(): Promise<Geofence[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/geofences/`);
    return response.data;
  },

  /**
   * Get a specific geofence
   */
  async getGeofence(id: number): Promise<Geofence> {
    const response = await apiService.get(`${GPS_BASE_URL}/geofences/${id}/`);
    return response.data;
  },

  /**
   * Create a new geofence
   */
  async createGeofence(data: GeofenceInput): Promise<Geofence> {
    const response = await apiService.post(`${GPS_BASE_URL}/geofences/`, data);
    return response.data;
  },

  /**
   * Update an existing geofence
   */
  async updateGeofence(id: number, data: Partial<GeofenceInput>): Promise<Geofence> {
    const response = await apiService.patch(`${GPS_BASE_URL}/geofences/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a geofence
   */
  async deleteGeofence(id: number): Promise<void> {
    await apiService.delete(`${GPS_BASE_URL}/geofences/${id}/`);
  },

  /**
   * Link devices to a geofence
   */
  async linkDevices(geofenceId: number, deviceIds: number[]): Promise<{
    message: string;
    linked_devices: number;
  }> {
    const response = await apiService.post(
      `${GPS_BASE_URL}/geofences/${geofenceId}/link_devices/`,
      { device_ids: deviceIds }
    );
    return response.data;
  },

  /**
   * Get geofence events (entry/exit)
   */
  async getEvents(geofenceId: number, hours: number = 24): Promise<{
    geofence_id: number;
    geofence_name: string;
    events: any[];
    from: string;
    to: string;
  }> {
    const response = await apiService.get(
      `${GPS_BASE_URL}/geofences/${geofenceId}/events/`,
      { params: { hours } }
    );
    return response.data;
  },
};

/**
 * Reports API
 */
export const reportsAPI = {
  /**
   * Get trip reports
   */
  async getTrips(filters: ReportFilters): Promise<TripReport[]> {
    const params: any = {
      from: filters.from instanceof Date ? filters.from.toISOString() : filters.from,
      to: filters.to instanceof Date ? filters.to.toISOString() : filters.to,
    };
    
    if (filters.device_id) {
      params.device_id = filters.device_id;
    }

    const response = await apiService.get(`${GPS_BASE_URL}/reports/trips/`, { params });
    return response.data;
  },

  /**
   * Get route report (positions)
   */
  async getRoute(deviceId: number, filters: DateRange): Promise<RouteReport> {
    const params = {
      device_id: deviceId,
      from: filters.from instanceof Date ? filters.from.toISOString() : filters.from,
      to: filters.to instanceof Date ? filters.to.toISOString() : filters.to,
    };

    const response = await apiService.get(`${GPS_BASE_URL}/reports/routes/`, { params });
    return response.data;
  },

  /**
   * Get summary reports
   */
  async getSummary(filters: ReportFilters): Promise<SummaryReport[]> {
    const params: any = {
      from: filters.from instanceof Date ? filters.from.toISOString() : filters.from,
      to: filters.to instanceof Date ? filters.to.toISOString() : filters.to,
    };
    
    if (filters.device_id) {
      params.device_id = filters.device_id;
    }

    const response = await apiService.get(`${GPS_BASE_URL}/reports/summary/`, { params });
    return response.data;
  },

  /**
   * Get stops reports
   */
  async getStops(filters: ReportFilters): Promise<StopsReport[]> {
    const params: any = {
      from: filters.from instanceof Date ? filters.from.toISOString() : filters.from,
      to: filters.to instanceof Date ? filters.to.toISOString() : filters.to,
    };
    
    if (filters.device_id) {
      params.device_id = filters.device_id;
    }

    const response = await apiService.get(`${GPS_BASE_URL}/reports/stops/`, { params });
    return response.data;
  },

  /**
   * Get events reports
   */
  async getEvents(filters: ReportFilters): Promise<any[]> {
    const params: any = {
      from: filters.from instanceof Date ? filters.from.toISOString() : filters.from,
      to: filters.to instanceof Date ? filters.to.toISOString() : filters.to,
    };
    
    if (filters.device_id) {
      params.device_id = filters.device_id;
    }

    const response = await apiService.get(`${GPS_BASE_URL}/reports/events/`, { params });
    return response.data;
  },

  /**
   * Export report to CSV
   */
  async exportToCSV(
    reportType: 'trips' | 'summary' | 'stops',
    filters: ReportFilters
  ): Promise<Blob> {
    const params: any = {
      from: filters.from instanceof Date ? filters.from.toISOString() : filters.from,
      to: filters.to instanceof Date ? filters.to.toISOString() : filters.to,
      format: 'csv',
    };
    
    if (filters.device_id) {
      params.device_id = filters.device_id;
    }

    const response = await apiFetchBlob(
      `${GPS_BASE_URL}/reports/${reportType}/`,
      { params }
    );
    return response.data;
  },
};

/**
 * Alerts API (Read-only + acknowledge - rule creation in admin)
 */
export const alertsAPI = {
  /**
   * Get all alert events
   */
  async listEvents(): Promise<AlertEvent[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/alert-events/`);
    return response.data;
  },

  /**
   * Get unacknowledged alert events
   */
  async getUnacknowledged(): Promise<AlertEvent[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/alert-events/unacknowledged/`);
    return response.data;
  },

  /**
   * Acknowledge an alert event
   */
  async acknowledgeEvent(id: number): Promise<{ message: string }> {
    const response = await apiService.post(`${GPS_BASE_URL}/alert-events/${id}/acknowledge/`);
    return response.data;
  },

  /**
   * Get alert rules (read-only)
   */
  async listRules(): Promise<AlertRule[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/alert-rules/`);
    return response.data;
  },
};

/**
 * Alert Rules Configuration API
 */
export const alertRulesAPI = {
  /**
   * Get all alert rules for the company
   */
  async listRules(): Promise<any[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/alert-rules/`);
    return response.data;
  },

  /**
   * Get available alert types with descriptions
   */
  async getAlertTypes(): Promise<any[]> {
    const response = await apiService.get(`${GPS_BASE_URL}/alert-rules/alert_types/`);
    return response.data;
  },

  /**
   * Create a new alert rule
   */
  async createRule(data: any): Promise<any> {
    const response = await apiService.post(`${GPS_BASE_URL}/alert-rules/`, data);
    return response.data;
  },

  /**
   * Update an existing alert rule
   */
  async updateRule(id: number, data: any): Promise<any> {
    const response = await apiService.patch(`${GPS_BASE_URL}/alert-rules/${id}/`, data);
    return response.data;
  },

  /**
   * Delete an alert rule
   */
  async deleteRule(id: number): Promise<void> {
    await apiService.delete(`${GPS_BASE_URL}/alert-rules/${id}/`);
  },
};

/**
 * License Check API
 * Note: Frontend uses getCompanyLicenses() from apiService.ts
 * which calls /api/companies/me/licenses/ to check GPS licenses
 */
export const licenseAPI = {};

/**
 * Traccar Direct Connection API
 */
export const traccarAPI = {
  /**
   * Get Traccar session token for direct WebSocket connection
   * Token is valid for 4 hours
   */
  async getTraccarToken(): Promise<{
    token: string;
    ws_url: string;
    expires_at: string;
    user_id: number;
    group_id: number;
  }> {
    const response = await apiService.post(`${GPS_BASE_URL}/live-tracking/get-traccar-token/`);
    return response.data;
  },
};

// Default export with all APIs
const gpsTrackingAPI = {
  devices: deviceAPI,
  liveTracking: liveTrackingAPI,
  geofences: geofenceAPI,
  reports: reportsAPI,
  alerts: alertsAPI,
  alertRules: alertRulesAPI,
  license: licenseAPI,
  traccar: traccarAPI,
};

export default gpsTrackingAPI;

